import { useState, useContext } from "react";
import CardDetailLayout from "../components/CardDetailLayout";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { AlertCircle, Settings } from "lucide-react";
import PublishButton from "../components/PublishButton";
import RevertToFactoryButton from "../components/RevertToFactoryButton";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Button } from "@/modern/components/ui/button";
import TestPromptButton from "../components/TestPromptButton";
import { OpenAIKeyModal } from "../components/OpenAIKeyModal";
import { isIAmOneOfBoardOwner } from "@/other/utils/utils";

const AIPromptDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { user, project, boardManager, selectedBoard } = useContext(BryntumContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Subscribe to board preferences changes to trigger rerender if open api key flag changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    }
  });

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Permission check: board owner only, excluding demo users
  // @ts-expect-error - boardManager is dynamically set in bryntumContext
  const boardStore = boardManager?.boardNameStore?.getById(selectedBoard?.id);
  const isBoardOwner = project && boardStore ? isIAmOneOfBoardOwner(project, boardStore.owner) : false;
  const canWrite = isBoardOwner && !user?.isDemoUser;

  return (
    <div className="flex flex-col h-full">
      {hasOpenAIKey && (
        <div className="px-6 pt-6 flex justify-end pb-4 gap-2">
          <RevertToFactoryButton />
          <TestPromptButton />
        </div>
      )}
      {hasOpenAIKey && <PublishButton />}
      {!hasOpenAIKey && (
        <div className="mx-6 mt-6 mb-0 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">OpenAI API Key Required</h4>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  To configure AI Prompt settings, you need to set up your OpenAI API key first.
                  {canWrite ? " Click the button to configure your API key." : " Only the Board Owner can configure the API key."}
                </p>
              </div>
            </div>
            {canWrite && (
              <Button onClick={() => setIsModalOpen(true)} size="sm" variant="default" className="flex-shrink-0">
                <Settings className="h-4 w-4 mr-2" />
                Configure Key
              </Button>
            )}
          </div>
        </div>
      )}
      <CardDetailLayout subcategory={subcategory} disableEditing={!hasOpenAIKey} />

      {/* OpenAI API Key Configuration Modal */}
      <OpenAIKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AIPromptDetail;
